// Shim: every ported screen takes `setView(viewId)` per the prototype's
// app.jsx prop-drilling pattern. In the SPA we use TanStack Router instead.
// This hook returns a `setView` that maps prototype view IDs → SPA routes.
// When a screen calls setView('create'), it navigates to /campaigns/new.
// Phase D / Phase C will replace these with proper `<Link>`s + router-driven
// state; for now this keeps the screens working unmodified.

import { useNavigate } from '@tanstack/react-router';
import { captureError } from '@/lib/telemetry';

const VIEW_TO_ROUTE: Record<string, string> = {
  'dashboard':    '/campaigns',
  'create':       '/campaigns/new',
  'audiences':    '/audiences',
  'new-audience': '/audiences/new',
  'creatives':    '/creatives',
  'new-creative': '/creatives/new',
  'reports':      '/reports',
  'spillover':    '/reports/perf-halo',
  'perf-halo':    '/reports/perf-halo',
  'delivery':     '/reports/delivery',
  'markup':       '/reports/markup',
  'productions':  '/backend/productions',
  'events':       '/backend/events',
  'venues':       '/backend/venues',
  'pixel':        '/backend/pixels',
  'capi':         '/backend/capi',
  'backfill':     '/backend/backfill',
  'admin-users':  '/admin/users',
  'admin-roles':  '/admin/roles',
  'admin-config': '/admin/config',
  'admin-logs':   '/admin/logs',
  'backend-alerts': '/backend/alerts',
  'admin-audit':  '/admin/audit',
};

export function useSetViewShim() {
  const navigate = useNavigate();
  return (viewId: string) => {
    const route = VIEW_TO_ROUTE[viewId];
    if (!route) {
      // A dead-end navigation: the button does nothing and the user is simply
      // stuck. `console.warn` reaches whoever has devtools open, which in
      // production is nobody.
      captureError({
        level: 'warn',
        error_kind: 'unknown_view_id',
        message: `setView shim: unknown view id ${viewId}`,
      });
      return;
    }
    void navigate({ to: route as never });
  };
}
